import type { Pattern } from "../types.js";

/**
 * REAL CONTENT — Enneatype 6. Third pattern of the alpha.
 *
 * Source: `docs/canonical/Plantilla_maestra_nueve_eneatipos.docx`, section 3,
 * «Eneatipo 6 — El Dubitativo», Bloque A. Same master template as 1 and 9.
 *
 * WHY 6 IS IN THE ALPHA, and it is a deliberate scope decision, not creep.
 * The alpha was 1 and 9 because v1.1 picked «dos patrones deliberadamente
 * opuestos» as a STRUCTURAL TEST of the template. Checked pair by pair across
 * the nine-type document: neither 1 nor 9 lists the other as confusable —
 * the pair does not exist in the canon, so the alpha was validating the easy
 * case and never exercising the differentiation machinery at all.
 *
 * Type 6 is the ONLY type the material crosses with BOTH:
 *   1↔6 — both anticipate problems and check details before acting.
 *         «¿está bien hecho?» favours 1; «¿es seguro?» favours 6.
 *   9↔6 — both hesitate and postpone decisions.
 *         Postponing because «da igual la opción» favours 9; postponing to
 *         gather information about risk favours 6.
 * Both Bloque E entries are already written, so adding 6 invents nothing.
 *
 * English, with the Spanish original beside each field, same as 1 and 9.
 *
 * NOTE ON `confusablePatterns`. The canon writes type 6's own confusables as
 * 5 («ambos cautelosos antes de actuar») and 7 («ambos con la mente ocupada»),
 * neither of which exists in the alpha. The 1 and 9 listed here come from the
 * OTHER side of each pair, where the canon does write them — confusability is
 * symmetric in reality even where the document only recorded one direction.
 * Types 5 and 7 go in when they exist, not before: pointing the result screen
 * at a pattern that isn't there is a dead end.
 */
export const PATTERN_6: Pattern = {
  id: "6",
  number: 6,
  // «el precavido» — Vilaseca usa "el dubitativo".
  workingName: "The wary one (working name)",
  // «Patrón organizado alrededor de anticipar el peligro y buscar certeza o
  //  apoyo externo antes de decidir»
  shortHypothesis:
    "A pattern organised around anticipating danger and looking for certainty " +
    "or outside backing before deciding.",
  // «Duda crónica, ansiedad anticipatoria, oscilación entre sumisión y desafío
  //  a la autoridad»
  whatItExplains:
    "Chronic doubt, anticipatory anxiety, and swinging between deferring to " +
    "authority and pushing back against it.",
  // «El contenido concreto de qué considera cada persona peligroso; el origen
  //  de una desconfianza puntual hacia alguien»
  whatItDoesNotExplain:
    "What any given person actually counts as dangerous, or where one " +
    "specific mistrust of one specific person came from.",
  // «Que la persona se defina como "la ansiosa/indecisa de siempre" y use la
  //  etiqueta para evitar decidir»
  identityRisk:
    "That you settle into “I'm the anxious, indecisive one” and use the " +
    "label as a reason not to decide.",
  confusablePatterns: ["1", "9"],
  sourceRefs: [
    "Plantilla_maestra_nueve_eneatipos.docx §3, Eneatipo 6, Bloque A",
    "Vilaseca, chapter “Eneatipo 6” — [FUENTE PROYECTO]",
    "Working name and short formulation are [PROPUESTA], not established fact",
    "[LÍMITE] Vilaseca-centric: not yet triangulated against a second source, " +
      "unlike patterns 1 and 9"
  ],
  contentVersion: "canon-nueve-1.0"
};
